import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Award, Truck, Shield, Star } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Home() {
  const [popularProducts, setPopularProducts] = useState([]);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    fetchPopularProducts();
    fetchReviews();
  }, []);

  const fetchPopularProducts = async () => {
    try {
      const response = await axios.get(`${API}/products/popular/list`);
      setPopularProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch popular products:', error);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews`);
      setReviews(response.data);
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[600px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1757961048415-3f965cd2c802"
            alt="Свежие устрицы"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 to-sky-900/60"></div>
        </div>

        <div className="relative z-10 text-center text-white max-w-4xl mx-auto px-4">
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 leading-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }} data-testid="hero-title">
            Свежие морепродукты<br />
            <span className="text-sky-300">премиум-качества</span>
          </h1>
          <p className="text-lg sm:text-xl mb-8 text-gray-200">
            Прямые поставки от лучших производителей с доставкой в термобоксах
          </p>
          <Link to="/catalog" data-testid="catalog-button">
            <Button className="bg-sky-600 hover:bg-sky-700 text-white px-8 py-6 text-lg rounded-full shadow-2xl">
              Перейти в каталог
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Advantages */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-8 rounded-2xl bg-gradient-to-br from-sky-50 to-blue-50 hover:shadow-xl transition-shadow" data-testid="advantage-quality">
              <div className="w-16 h-16 bg-sky-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Гарантия свежести</h3>
              <p className="text-gray-600">Только свежие продукты от проверенных поставщиков</p>
            </div>
            <div className="text-center p-8 rounded-2xl bg-gradient-to-br from-cyan-50 to-teal-50 hover:shadow-xl transition-shadow" data-testid="advantage-delivery">
              <div className="w-16 h-16 bg-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Truck className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Доставка в термобоксах</h3>
              <p className="text-gray-600">Сохраняем правильную температуру при доставке</p>
            </div>
            <div className="text-center p-8 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 hover:shadow-xl transition-shadow" data-testid="advantage-suppliers">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Проверенные поставщики</h3>
              <p className="text-gray-600">Работаем только с лучшими производителями</p>
            </div>
          </div>
        </div>
      </section>

      {/* Popular Products */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }} data-testid="popular-products-title">
              Популярные товары
            </h2>
            <p className="text-gray-600">Наши бестселлеры</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-testid="popular-products-grid">
            {popularProducts.slice(0, 8).map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>

          <div className="text-center">
            <Link to="/catalog" data-testid="view-all-products-button">
              <Button variant="outline" className="border-2 border-sky-600 text-sky-600 hover:bg-sky-600 hover:text-white px-8 py-6 text-lg rounded-full">
                Смотреть все товары
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Promotions */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Актуальные акции
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="relative rounded-2xl overflow-hidden group h-64">
              <img
                src="https://images.unsplash.com/photo-1757961048219-df1bbd0be167"
                alt="Акция"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex flex-col justify-end p-8">
                <h3 className="text-2xl font-bold text-white mb-2">Скидка 10% на икру</h3>
                <p className="text-white/90 mb-4">При покупке от 5000 рублей</p>
                <Link to="/promotions">
                  <Button className="bg-white text-gray-900 hover:bg-gray-100">Подробнее</Button>
                </Link>
              </div>
            </div>

            <div className="relative rounded-2xl overflow-hidden group h-64">
              <img
                src="https://images.unsplash.com/photo-1739991017889-009e4fc9cdc6"
                alt="Акция"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex flex-col justify-end p-8">
                <h3 className="text-2xl font-bold text-white mb-2">Бесплатная доставка</h3>
                <p className="text-white/90 mb-4">При заказе от 10000 рублей</p>
                <Link to="/promotions">
                  <Button className="bg-white text-gray-900 hover:bg-gray-100">Подробнее</Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }} data-testid="reviews-title">
              Отзывы клиентов
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="reviews-grid">
            {reviews.map(review => (
              <div key={review.id} className="bg-white p-6 rounded-2xl shadow-lg">
                <div className="flex mb-3">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-600 mb-4 line-clamp-4">{review.comment}</p>
                <p className="font-semibold text-gray-900">{review.user_name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}